import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  message?: ReactNode;
  children?: ReactNode;
  dismissable?: boolean;
  showCloseButton?: boolean;
  width?: number | string;
}

export function Dialog({
  open,
  onClose,
  title,
  message,
  children,
  dismissable = true,
  showCloseButton = true,
  width,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && dismissable) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismissable, onClose]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      const el = dialogRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      el?.focus();
    }, 50);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="dialog-backdrop"
      onClick={() => {
        if (dismissable) onClose();
      }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="apple-dialog-title"
        aria-describedby={message ? 'apple-dialog-msg' : undefined}
        className="dialog"
        style={width ? { width: typeof width === 'number' ? `${width}px` : width } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div id="apple-dialog-title" className="dialog-title">{title}</div>
            {message ? <div id="apple-dialog-msg" className="dialog-message">{message}</div> : null}
          </div>
          {showCloseButton ? (
            <button
              type="button"
              aria-label="Close dialog"
              onClick={onClose}
              className="shrink-0 rounded-md p-1 text-[var(--color-quaternary-label)] transition hover:bg-white/5 hover:text-white cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        {children}
      </div>
    </div>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'destructive' | 'primary';
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'destructive',
  loading = false,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} title={title} message={description}>
      <div className="dialog-actions">
        <button
          type="button"
          className="btn btn-secondary cursor-pointer"
          onClick={onClose}
          disabled={loading}
        >
          {cancelLabel}
        </button>
        <button
          type="button"
          className={cn(
            'btn cursor-pointer',
            variant === 'destructive' ? 'btn-destructive' : 'btn-primary',
          )}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}